import { FC, useEffect, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useChatStore } from "../../../stores/ChatStore";
import moment from "moment-timezone";
import { KTSVG } from "../../../_metronic/helpers";
import useUser from "../../../hooks/User";
import CheckboxList from "./CheckboxList";
import { ChatMessageBubble } from "./ChatMessageBubble";
import { ChatMessage } from "../../../interfaces/Chat/Chat";

interface Props {
    handleIntSync: (isFirst: boolean, dataRoom: ChatMessage) => void;
}

const ChatMessageList: FC<Props> = ({ handleIntSync }) => {
    const {
        messages,
        phoneNumber,
        mode,
        showChat,
        scrollToId,
        setScrollToId,
        isOpenCheckBoxForward,
    } = useChatStore();

    const [isAtBottom, setIsAtBottom] = useState(true);
    const [dataLastChat, setDataLastChat] = useState<ChatMessage>({} as ChatMessage);
    const user = useUser();
    const parentRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: messages.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 250,
        overscan: 20,
    });

    const items = virtualizer.getVirtualItems();

    const scrollToChat = (index: number) => {
        if (!virtualizer.scrollElement) return;
        virtualizer.scrollToIndex(index, { align: "center" });

        requestAnimationFrame(() => {
            const lastMessageElement = document.getElementById(`message-${index}`);
            if (lastMessageElement) {
                lastMessageElement.scrollIntoView({ behavior: "smooth", block: "center" });
            } else {
                virtualizer.scrollElement!.scrollTo({
                    top: virtualizer.scrollElement!.scrollHeight,
                    behavior: "smooth",
                });
            }
        });
    };

    const handleScrollToButtonClick = () => {
        scrollToChat(messages.length - 1);
        setIsAtBottom(true);
    };

    const handleSyncMessage = (data: ChatMessage) => {
        let resultData = {} as ChatMessage
        if(data) {
            resultData = data
        } else {
            let newData = {
                id: "",
                message: "",
                receivedAt: moment().format("YYYY-MM-DD HH:mm:ss"),
                attachmentName: null,
                updatedAt: null,
                fromMe: false,
                idReference: null,
                isForwarded: false,
                isJMessageAvailable: false,
                sender: null,
                phone: phoneNumber.split('-')[0],
                as: phoneNumber.split('-')[1],
                action: {
                    isEditing: false
                }
            }

            resultData = newData
        }

        setDataLastChat(resultData)
        handleIntSync(false, resultData);
    };

    useEffect(() => {
        if (showChat && mode === "normal") {
            scrollToChat(messages.length - 1);
        } else if (showChat && scrollToId && mode === "search") {
            scrollToChat(messages.findIndex((message) => message.id === scrollToId));
            setScrollToId("");
        } else if (showChat && mode === "sync-room") {
            scrollToChat(messages.findIndex((message) => message.id === dataLastChat.id));
            setScrollToId("");
        }
    }, [phoneNumber, scrollToId, mode, messages, showChat]);

    useEffect(() => {
        const handleScroll = () => {
            const lastIndex = messages.length - 1;
            const lastItem = items.find((item) => item.index === lastIndex);

            const scrollTop = virtualizer.scrollElement?.scrollTop || 0;
            const scrollBottom =
                scrollTop + (virtualizer.scrollElement?.clientHeight || 0);

            const isLastMessageVisible =
                lastItem &&
                lastItem.start >= scrollTop &&
                lastItem.end <= scrollBottom;

            setIsAtBottom(messages.length === 0 ? true : !!isLastMessageVisible);
        };

        const scrollElement = parentRef.current;
        scrollElement?.addEventListener("scroll", handleScroll);
        return () => scrollElement?.removeEventListener("scroll", handleScroll);
    }, [items, messages.length, virtualizer]);

    return (
        <div
            ref={parentRef}
            className="card-body my-2"
            style={{
                height: "100%",
                overflowY: "auto",
                position: "relative",
                contain: "strict",
            }}
        >
            <div
                style={{
                    height: virtualizer.getTotalSize(),
                    width: "100%",
                    position: "relative",
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        transform: `translateY(${items[0]?.start ?? 0}px)`,
                    }}
                >
                    <div className="d-flex justify-content-center">
                        <button
                            style={{ borderRadius: "50%" }}
                            type="button"
                            className="btn btn-light btn-sm btn-icon mb-3"
                            onClick={() => handleSyncMessage(messages[0])}
                        >
                            <i className="fas fa-redo-alt fs-4 menu-icon" />
                        </button>
                    </div>

                    {items.map((virtualItem) => {
                        const index = virtualItem.index;
                        const message = messages[index];
                        const prevMessage = messages[index - 1];

                        const showDate =
                            index === 0 ||
                            moment(message.receivedAt).format("DD MMMM YYYY") !==
                            moment(prevMessage?.receivedAt).format("DD MMMM YYYY");

                        const displayDate = () => {
                            const now = moment();
                            const msgTime = moment(message.receivedAt, "YYYY-MM-DD HH:mm:ss");
                            const diff = now.startOf("day").diff(msgTime.startOf("day"), "days");

                            if (diff === 0) return "TODAY";
                            if (diff === 1) return "YESTERDAY";
                            if (now.isoWeek() === msgTime.isoWeek() && diff < 7)
                                return msgTime.format("dddd").toUpperCase();

                            return msgTime.format("DD MMMM YYYY").toUpperCase();
                        };

                        return (
                            <div
                                key={index}
                                id={`message-${index}`}
                                data-index={index}
                                ref={virtualizer.measureElement}
                                className={`message-item ${isOpenCheckBoxForward ? "message-with-checkbox" : ""}`}
                            >
                                {showDate && (
                                    <div className="d-flex justify-content-center">
                                        <span className="badge badge-light-primary fs-7 fw-bold mb-2">
                                            {displayDate()}
                                        </span>
                                    </div>
                                )}

                                <div className="position-relative">
                                    {isOpenCheckBoxForward && <CheckboxList message={message} />}
                                    <ChatMessageBubble
                                        message={message}
                                        key={message.id}
                                        scrollToChat={scrollToChat}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {!isAtBottom && (
                <span
                    style={{
                        position: "sticky",
                        bottom: 0,
                        right: 0,
                    }}
                >
                    <p className="text-end">
                        <span
                            style={{
                                cursor: "pointer",
                                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                            }}
                            className="bg-white border border-1 py-6 px-4 rounded"
                            onClick={handleScrollToButtonClick}
                        >
                            <KTSVG
                                path="media/icons/duotune/arrows/arr004.svg"
                                className="svg-icon-dark svg-icon-2x"
                            />
                        </span>
                    </p>
                </span>
            )}
        </div>
    );
};

export { ChatMessageList };
